/**
 * useWorkflowConditionRefs - converts the entity catalog into the
 * `AvailableRef[]` shape the shared <ConditionBuilder> consumes.
 *
 * Workflows are now bound from entity attributes, so the condition UI
 * starts from "which entity points at this workflow?" instead of the
 * deprecated workflow.resource_type field. A resource-type field catalog
 * remains as a fallback for older workflows during the transition.
 */

import { useMemo } from 'react'

import {
  useEntityTypes,
  type EntityAttributeDefinition,
  type EntityFieldType,
  type EntityTypeDefinition,
} from '@/admin/hooks/use-entities'
import type {
  AvailableRef,
  RefType,
} from '@/admin/components/condition-builder/types'
import {
  useResourceTypeFields,
  type ResourceTypeFieldRef,
} from '@/admin/hooks/use-workflow-definitions'

export interface UseResourceRefsResult {
  refs: AvailableRef[]
  byRef: (ref: string) => AvailableRef | undefined
  isLoading: boolean
}

interface WorkflowConditionRefsOptions {
  workflowKey?: string
  resourceType?: string
}

export function useResourceRefs(
  resourceType: string | undefined,
): UseResourceRefsResult {
  return useWorkflowConditionRefs({ resourceType })
}

export function useWorkflowConditionRefs({
  workflowKey,
  resourceType,
}: WorkflowConditionRefsOptions): UseResourceRefsResult {
  const { data: entities = [], isLoading: entitiesLoading } = useEntityTypes()
  const { data, isLoading } = useResourceTypeFields(resourceType)

  const refs = useMemo<AvailableRef[]>(() => {
    const attachedEntity = findAttachedEntity(entities, {
      workflowKey,
      resourceType,
    })
    const entityRefs = attachedEntity
      ? buildEntityRefs(attachedEntity, entities)
      : []
    const resourceRefs = (data?.fields ?? []).map((f) => toAvailableRef(f))
    return dedupeRefs([...entityRefs, ...resourceRefs])
  }, [data?.fields, entities, resourceType, workflowKey])

  const byRef = useMemo(() => {
    const map = new Map(refs.map((r) => [r.ref, r]))
    return (ref: string) => map.get(ref)
  }, [refs])

  return { refs, byRef, isLoading: entitiesLoading || isLoading }
}

function toAvailableRef(field: ResourceTypeFieldRef): AvailableRef {
  const options = field.choices?.map((value) => ({ value, label: value }))
  return {
    ref: field.ref,
    label: field.label,
    category: 'registration_field',
    type: options?.length ? 'enum' : mapType(field.type),
    options,
  }
}

function findAttachedEntity(
  entities: EntityTypeDefinition[],
  {
    workflowKey,
    resourceType,
  }: {
    workflowKey?: string
    resourceType?: string
  },
): EntityTypeDefinition | undefined {
  if (workflowKey) {
    const configured = entities.find((entity) =>
      entity.attributes.some((attribute) => {
        const config = attribute.configuration_data ?? {}
        const configuredWorkflow = config.workflow_key || attribute.code_name
        return (
          config.value_source === 'workflow_state' &&
          configuredWorkflow === workflowKey
        )
      }),
    )
    if (configured) return configured
  }
  return resourceType
    ? entities.find((entity) => entity.key === resourceType)
    : undefined
}

function buildEntityRefs(
  root: EntityTypeDefinition,
  entities: EntityTypeDefinition[],
): AvailableRef[] {
  const byKey = new Map(entities.map((entity) => [entity.key, entity]))
  const refs: AvailableRef[] = []
  const queue: Array<{
    entity: EntityTypeDefinition
    labelPath: string[]
    isMany: boolean
    depth: number
  }> = [{ entity: root, labelPath: [root.label], isMany: false, depth: 0 }]
  const visited = new Set<string>()

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current || visited.has(current.entity.key)) continue
    visited.add(current.entity.key)

    refs.push(
      ...current.entity.attributes.map((attribute) =>
        toEntityRef(attribute, current),
      ),
    )

    if (current.depth >= 3) continue
    for (const relationship of current.entity.relationships) {
      const target = byKey.get(relationship.target_key)
      if (!target || visited.has(target.key)) continue
      queue.push({
        entity: target,
        labelPath: [...current.labelPath, relationship.label],
        isMany: current.isMany || relationship.cardinality === 'MANY',
        depth: current.depth + 1,
      })
    }
  }

  refs.push({
    ref: 'field:today',
    label: 'Today',
    category: 'computed',
    type: 'date',
  })

  return refs
}

function toEntityRef(
  attribute: EntityAttributeDefinition,
  current: {
    entity: EntityTypeDefinition
    labelPath: string[]
    isMany: boolean
  },
): AvailableRef {
  return {
    ref: `entity:${current.entity.key}.${attribute.code_name}`,
    label: `${current.labelPath.join(' / ')}: ${attribute.display_name}`,
    category: 'entity',
    type: current.isMany
      ? 'multi_enum'
      : attribute.configuration_data?.options?.length
        ? 'enum'
        : mapEntityFieldType(attribute.field_type),
    options: attribute.configuration_data?.options,
  }
}

function dedupeRefs(refs: AvailableRef[]): AvailableRef[] {
  const seen = new Set<string>()
  return refs.filter((ref) => {
    if (seen.has(ref.ref)) return false
    seen.add(ref.ref)
    return true
  })
}

function mapType(raw: string): RefType {
  switch (raw) {
    case 'number':
    case 'integer':
    case 'float':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'date':
    case 'datetime':
      return 'date'
    case 'enum':
      return 'enum'
    case 'multi_enum':
      return 'multi_enum'
    default:
      return 'string'
  }
}

function mapEntityFieldType(fieldType: EntityFieldType): RefType {
  switch (fieldType) {
    case 'NUMBER':
    case 'MONEY':
      return 'number'
    case 'BOOLEAN':
      return 'boolean'
    case 'DATE':
    case 'DATE_TIME':
      return 'date'
    case 'SELECT':
      return 'enum'
    case 'MULTI_SELECT':
    case 'DOUBLE_SELECT':
      return 'multi_enum'
    default:
      return 'string'
  }
}
